/**
 * Renders expression graph as canonical S-Expression string for debug logging.
 * Format is defined in docs/s-expression-format.md and must match backend debug_s_expression.py.
 */
import type { ExpressionConstant, ExpressionGraph, Indicator, Instrument } from '../state/types';

function quoteString(s: string): string {
  return '"' + s.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
}

function labelNeedsQuotes(label: string): boolean {
  return /[.() \t\n\r]/.test(label);
}

export function sExpressionString(
  graph: ExpressionGraph,
  indicators: Indicator[],
  instruments: Instrument[],
  constants: ExpressionConstant[] = []
): string {
  const constantsById = new Map(constants.map((c) => [c.id, c]));

  function render(id: string | null | undefined): string {
    if (id == null || id === '') return '?';
    const node = graph.expressions[id];
    if (!node) return '?';

    if (node.type === 'function') {
      const operands = node.operandIds.map(render);
      const parts = [node.functionId, ...operands];
      if (node.epsilon != null && node.epsilon !== undefined) {
        parts.push(`:epsilon ${node.epsilon}`);
      }
      return `(${parts.join(' ')})`;
    }

    if (node.type === 'ohlc_series') {
      const symbol = instruments.find((inst) => inst.id === node.seriesId)?.symbol ?? '?';
      const field = node.field ?? '?';
      return `(ohlc ${symbol} ${field} ${node.offset})`;
    }

    if (node.type === 'indicator_series') {
      const indicator = indicators?.find((ind) => ind.id === node.seriesId);
      if (!indicator) {
        return `(indicator ? ? ${node.offset})`;
      }
      let label: string = indicator.name;
      if (labelNeedsQuotes(label)) {
        label = quoteString(label);
      }
      const field = node.field ?? 'value';
      return `(indicator ${label} ${field} ${node.offset})`;
    }

    if (node.type === 'literal') {
      if (typeof node.value === 'number') {
        return String(node.value);
      }
      return quoteString(String(node.value));
    }

    if (node.type === 'constant') {
      const constant = constantsById.get(node.constantId);
      if (!constant) return '?';
      if (typeof constant.value === 'number') {
        return String(constant.value);
      }
      return quoteString(String(constant.value));
    }

    if (node.type === 'seriesAware') {
      const instrument = instruments.find((inst) => inst.id === node.seriesId);
      const indicator = indicators?.find((ind) => ind.id === node.seriesId);
      const fields = node.requiredFields?.length ? node.requiredFields.join(' ') : '?';
      const offset = node.offset ?? 0;
      if (instrument) {
        return `(seriesAware ohlc ${instrument.symbol} ${fields} ${offset})`;
      }
      if (indicator) {
        let label: string = indicator.name;
        if (labelNeedsQuotes(label)) {
          label = quoteString(label);
        }
        return `(seriesAware indicator ${label} ${fields} ${offset})`;
      }
      return `(seriesAware ? ${fields} ${offset})`;
    }

    return '?';
  }

  if (graph.rootId == null || graph.rootId === '') {
    return '?';
  }
  return render(graph.rootId);
}
